import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Colors, Dimensions } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Title, Text } from '@/shared/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRegistration } from '@/context/RegistrationContext';
import { WREG_ROUTE, wregCarriedSearch } from '@/components/webreg/wregRoutes';
import { wregHasProgress, wregResumeStep } from '@/components/webreg/wregResume';
import { useApiError } from '@/context/ApiErrorContext';
import { apiClient } from '@/services/api';
import { env } from '@/env';
import {
  VIEW_WELCOME,
  START_ONBOARDING,
  NEED_HELP,
  EVENT_SOURCES,
  ERROR_API_CALL,
  WREG_FAILURE_REASONS,
  WREG_REGISTRATION_FAILED,
} from '@/consts/events';
import { signupErrStatus, signupPromoRejected } from '@/components/webreg/useWregSignup';

import heroImage from '@/assets/images/Landing/hero-monitor.jpg';

import iconMail from '@/assets/images/Landing/icon-mail.svg';
import iconPhone from '@/assets/images/Landing/icon-phone.svg';
import { redirect } from '@/navigation';

// The landing page can support its two-column layout well before the wider
// registration-grid breakpoint. Keeping the 1000px breakpoint here made the
// square hero remain full-height in split-screen and small desktop windows.
const LANDING_MOBILE_MAX_WIDTH_PX = 599;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Was width:100vw, which is wider than the visible viewport whenever a
// vertical scrollbar is present (100vw includes the scrollbar gutter) -
// clamp to 100% of the parent (Container is already centered/full-width) so
// the hero never overflows horizontally.
const HeroSection = styled.div`
  width: 100%;
  height: 428px;
  max-width: var(--max-screen-width);

  @media (max-width: ${LANDING_MOBILE_MAX_WIDTH_PX}px) {
    height: auto;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  background: #fff;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: min(${Dimensions.c10}px, 100vw);
  /* Unconditional, not mobile-only: max-width resolves to 100vw for any viewport under 784px, so
     between 600 and 783 the container is exactly as wide as the screen and gets no centering
     margin. Gating this padding on the mobile breakpoint left the headline, CTA, disclaimer and
     support box flush against both bezels across that whole band - iPad portrait, Android tablets
     and iPhones in landscape. box-sizing: border-box keeps the 784px cap intact above it. */
  padding: 0 ${Spacing.x2}px ${Spacing.x5}px;
  box-sizing: border-box;
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    'copy controls'
    'copy disclaimer';
  column-gap: ${Spacing.x5}px;
  row-gap: ${Spacing.x2}px;
  align-items: flex-start;
  width: 100%;
  margin-top: ${Spacing.x5}px;

  @media (max-width: ${LANDING_MOBILE_MAX_WIDTH_PX}px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'controls'
      'copy'
      'disclaimer';
    gap: ${Spacing.x4}px;
    margin-top: ${Spacing.x2}px;
  }
`;

const CopyColumn = styled.div`
  grid-area: copy;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${Spacing.x4}px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  width: 100%;
`;

const CTAControls = styled.div`
  grid-area: controls;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${Spacing.x2}px;
  align-items: center;
`;

const CTAButton = styled.button<{ $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.x1}px;
  width: 100%;
  height: 56px;
  padding: 14px ${Spacing.x4}px;
  background-color: ${({ $isLoading }) =>
    $isLoading ? BorisScreener.coralPressed : BorisScreener.coral};
  color: ${Colors.background};
  font-family: Lato;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  border: none;
  border-radius: 24px;
  cursor: ${({ $isLoading }) => ($isLoading ? 'default' : 'pointer')};

  &:hover {
    background-color: ${BorisScreener.coralPressed};
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${Colors.background};
  border-top-color: transparent;
  border-radius: 50%;
`;

const DisclaimerSection = styled.div`
  grid-area: disclaimer;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x3}px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const SupportBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  background-color: ${Colors.backgroundAlt};
  border-radius: 12px;
  padding: ${Spacing.x2}px;
  width: 100%;
  box-sizing: border-box;
`;

const SupportLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${Spacing.x1}px;
  color: ${Colors.primary};
  text-decoration: none;
  font-family: Lato;
  font-size: 16px;
  line-height: 24px;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const SupportIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const StartOverButton = styled.button`
  font-family: Lato;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${Colors.primary};
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const MobileCenteredTitle = styled(Title)`
  @media (max-width: ${LANDING_MOBILE_MAX_WIDTH_PX}px) {
    text-align: center;
  }
`;

const MobileCenteredText = styled(Text)`
  @media (max-width: ${LANDING_MOBILE_MAX_WIDTH_PX}px) {
    text-align: center;
  }
`;

function getSupportEmailByPromoCode(promoCode: string) {
  return env.SUPPORT_EMAIL.replace('%@', promoCode);
}

const LandingStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackView, track, trackError } = useAnalytics();
  const { clearRegistrationData, setPromoCodeData, registrationData, isLoaded } = useRegistration();
  const [searchParams] = useSearchParams();
  const inviteMode = searchParams.get('invite') === 'true';
  const { showApiError } = useApiError();
  const [isCheckingPromoCode, setIsCheckingPromoCode] = useState(false);
  // `?promo=` selects the client for this registration; without it, the code the registration is
  // already running on (RND-38773), and failing that the build's own code.
  // Added for the go-live production test (RND-38696): a tester needs to land in the capped test
  // client rather than the production one, and the CMS Integration Service uses the same code as the
  // second condition on routing a named test MBI to the CMS sandbox. hello-server still validates
  // the code and enforces its cap, so an unknown one rejects here exactly like a bad build value.
  const paramPromoCode = searchParams.get('promo')?.trim();
  const entryPromoCode = paramPromoCode || env.ACCESS_PILOT_PROMO_CODE;
  const promoCode =
    paramPromoCode ||
    (isLoaded ? registrationData.promoCodeData?.code : undefined) ||
    entryPromoCode;

  const hasProgress = isLoaded && wregHasProgress(registrationData);
  // RND-37692: Landing navigated with a bare pathname, so a member bounced here mid-flow lost
  // `?invite=true` and the harness params on the way back in. Carry what the flow carries.
  const carriedSearch = wregCarriedSearch(`?${searchParams.toString()}`);
  const resumePath = `${hasProgress ? WREG_ROUTE[wregResumeStep(registrationData)] : WREG_ROUTE.intake}${carriedSearch}`;
  const ctaLabel = hasProgress ? t('landing.cta-resume') : t('landing.cta');

  const checkPromoCodeAndNavigate = useCallback(
    async (target: string, code: string) => {
      try {
        setIsCheckingPromoCode(true);

        // The default (env) promo code for the non-invite path. Chief
        // recognizing the code IS the validity signal — an unknown code
        // rejects with InvalidPromoCode and lands in the catch below.
        const response = await apiClient.promoCode(code);

        // Store promo code data in context
        setPromoCodeData(response);

        setIsCheckingPromoCode(false);

        if (response.capReached === true) {
          track({
            event: WREG_REGISTRATION_FAILED,
            properties: { reason: WREG_FAILURE_REASONS.PROMO_CAP_REACHED },
          });
          showApiError();
          return;
        }

        navigate(target);
      } catch (error) {
        console.error('Error checking promo code:', error);
        setIsCheckingPromoCode(false);
        // RND-38229: a cap can close the code itself, and this lookup is then the
        // first thing that fails — before the member has typed anything, so there
        // is no identity to record a CAP_REACHED against. The funnel event is what
        // is available here, and without it the whole flow ended silently.
        trackError({
          event: ERROR_API_CALL,
          properties: { service: 'wreg_promo', status: signupErrStatus(error) },
        });
        track({
          event: WREG_REGISTRATION_FAILED,
          properties: {
            reason: signupPromoRejected(error)
              ? WREG_FAILURE_REASONS.PROMO_CAP_REACHED
              : WREG_FAILURE_REASONS.CLIENT_ERROR,
          },
        });
        showApiError();
      }
    },
    [navigate, showApiError, setPromoCodeData, track, trackError],
  );

  // RND-37692: no clearRegistrationData() here. This effect ran on every arrival at `/`,
  // including the gate bounces and the catch-all redirect, so a URL typo or a refresh on a
  // gated screen silently destroyed a half-finished registration. The wipe is now the Start
  // Over control below and `WregFlowProvider.startOver`, both of which the user has to choose.
  useEffect(() => {
    trackView({ event: VIEW_WELCOME });
  }, [trackView]);

  // RND-36917: invited testers use the promo code they type on Medicare
  // Coverage, so Landing's default-code fetch is skipped for them. The param
  // is forwarded on the URL (not stored), so the field only shows while
  // ?invite=true is actually present.
  useEffect(() => {
    if (!inviteMode) return;
    navigate(`${WREG_ROUTE.intake}?invite=true`, { replace: true });
  }, [inviteMode, navigate]);

  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (!element) return;
      const header = document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }, []);

  const handleGetStarted = async () => {
    track({ event: START_ONBOARDING });
    await checkPromoCodeAndNavigate(resumePath, promoCode);
  };

  // Start Over re-enters through the promo fetch, exactly like Get Started. The wipe drops
  // promoCodeData with everything else, and `useWregSignup` sends `promoCode: ''` when it is
  // missing, which hello-server rejects — so entering step 1 without it is not an option.
  const handleStartOver = async () => {
    clearRegistrationData();
    await checkPromoCodeAndNavigate(`${WREG_ROUTE.intake}${carriedSearch}`, entryPromoCode);
  };

  const handleNeedHelp = (type: 'email' | 'phone') => {
    track({
      event: NEED_HELP,
      properties: { source: EVENT_SOURCES.WELCOME, type },
    });
    const email = getSupportEmailByPromoCode(entryPromoCode);
    redirect(`mailto:${email}`);
  };

  return (
    <Container>
      <HeroSection>
        <HeroImage src={heroImage} alt={t('landing.title')} />
      </HeroSection>

      <ContentContainer>
        <ContentSection id="check-eligibility">
          <CTAControls>
            <CTAButton
              onClick={handleGetStarted}
              $isLoading={isCheckingPromoCode}
              aria-label={ctaLabel}
              disabled={isCheckingPromoCode}
            >
              {isCheckingPromoCode ? <LoadingSpinner /> : ctaLabel}
            </CTAButton>

            {hasProgress && (
              <StartOverButton type="button" onClick={handleStartOver}>
                {t('landing.start-over')}
              </StartOverButton>
            )}
          </CTAControls>

          <CopyColumn>
            <TitleGroup>
              <MobileCenteredTitle size="h3" bold>
                {t('landing.title')}
              </MobileCenteredTitle>
              <MobileCenteredText size="p1">{t('landing.subtitle')}</MobileCenteredText>
            </TitleGroup>
            <MobileCenteredText size="p1">{t('landing.description')}</MobileCenteredText>
          </CopyColumn>

          <DisclaimerSection>
            <Text size="p2" centered>
              {t('landing.disclaimer-cta')}
            </Text>

            <SupportBox id="help">
              <Text size="p2">{t('landing.support-box-title')}</Text>
              <SupportLink onClick={() => handleNeedHelp('email')}>
                <SupportIcon src={iconMail} alt="" aria-hidden="true" />
                {t('landing.support-email')}
              </SupportLink>
              <SupportLink
                href={`tel:${env.SUPPORT_NUMBER}`}
                onClick={() => handleNeedHelp('phone')}
              >
                <SupportIcon src={iconPhone} alt="" aria-hidden="true" />
                {env.SUPPORT_NUMBER}
              </SupportLink>
            </SupportBox>
          </DisclaimerSection>
        </ContentSection>
      </ContentContainer>
    </Container>
  );
};

export { LandingStep };
